import {
  CategoryOperator,
  ClosingTimeTypeEnum,
  ComparisonOperator,
  ExchangeByRuleTypeEnum,
  PermissionTypeEnum,
  SortBy,
} from "@/pages/taskpane/shared/types/enums/globalEnum";
import { EDisableRule, EForceOpenOrder, EPermissionType } from "./enum";

export class ResourceEntity {
  allowRecurringMeetings: boolean;
  capacity: number;
  description: string;
  email: string;
  id: number;
  isFavoriteResource: boolean;
  location: string;
  name: string;
  resourceType: string;
  postBufferTime: number;
  preBufferTime: number;
  categories: CategoryInfo[];
  availability: AvailabilityEntity;
  openEndTime: string;
  openStartTime: string;
  closingDate: string;
  openingDate: string;
  bookingWindowInDays: number;
  maximumDurationInMinutes: number;
  isHotDesk: boolean;
  isLightResource: boolean;
  isPooledResource: boolean;
  // userRestriction: string;
  userRestriction: IUserRestriction | null;
  childResources: ChildResourcesEntity[];
  isOrdered?: boolean;
  imageUrl?: string;
  richDescription?: string | null;
  costInitial?: number;
  costPrMinute?: number;
  isExternal: boolean;

  constructor(data?: Partial<ResourceEntity>) {
    this.allowRecurringMeetings = data?.allowRecurringMeetings ?? true;
    this.id = data?.id ?? 0;
    this.email = data?.email?.toLowerCase().trim() ?? "";
    this.name = data?.name ?? "";
    this.resourceType = data?.resourceType ?? "";
    this.location = data?.location ?? "";
    this.description = data?.description ?? "";
    this.capacity = data?.capacity ?? 0;
    this.postBufferTime = data?.postBufferTime ?? 0;
    this.preBufferTime = data?.preBufferTime ?? 0;
    this.isFavoriteResource = data?.isFavoriteResource ?? false;
    this.categories = data?.categories ? data.categories.map((item) => new CategoryInfo(item)) : [];
    this.availability = new AvailabilityEntity(data?.availability, this.allowRecurringMeetings);
    // this.openEndTime = new OpenTimeEntity(data?.openEndTime);
    // this.openStartTime = new OpenTimeEntity(data?.openStartTime);
    this.openEndTime = data?.openEndTime ?? "";
    this.openStartTime = data?.openStartTime ?? "";
    this.closingDate = data?.closingDate ?? "";
    this.openingDate = data?.openingDate ?? "";
    this.bookingWindowInDays = data?.bookingWindowInDays ?? 0;
    this.maximumDurationInMinutes = data?.maximumDurationInMinutes ?? 0;
    this.isHotDesk = data?.isHotDesk ?? false;
    this.isLightResource = data?.isLightResource ?? false;
    this.isPooledResource = data?.isPooledResource ?? false;
    this.childResources = data?.childResources ? data.childResources.map((item) => new ChildResourcesEntity(item)) : [];
    this.userRestriction = data?.userRestriction ?? null;
    this.imageUrl = data?.imageUrl ?? "";
    this.richDescription = data?.richDescription ?? null;
    this.costInitial = data?.costInitial;
    this.costPrMinute = data?.costPrMinute;
    if(!this.email) {
      this.email = this.childResources.map((childResource) => childResource.email).join(",");
    }
    this.isExternal = !!data?.isExternal;
  }

  getDisableRule(selectedResource: ResourceEntity[]): EDisableRule[] {
    let disableRules: EDisableRule[] = [];
    if (this.isDisableResourceByPoolRule(selectedResource)) {
      disableRules.push(EDisableRule.DisablePooledResource);
    }
    if (this.isDisableResourceByVrRule(selectedResource)) {
      disableRules.push(EDisableRule.DisableVRResource);
    }
    return disableRules;
  }

  isDisableResourceByPoolRule(selectedResource: ResourceEntity[]): boolean {
    if (selectedResource?.length > 0 && this.isPooledResource) {
      return true;
    }
    return selectedResource.some((resource) => resource.isPooledResource);
  }

  isDisableResourceByVrRule(selectedResource: ResourceEntity[]): boolean {
    const selectedResourceEmail: string[] = [];
    selectedResource.forEach((resource) => {
      if (resource.email) {
        selectedResourceEmail.push(resource.email);
      }
      if (resource?.childResources?.length > 0) {
        resource.childResources.forEach((childResource) => {
          if (childResource.email) {
            selectedResourceEmail.push(childResource.email);
          }
        });
      }
    });

    if (selectedResourceEmail.length > 0) {
      return Boolean(
        selectedResourceEmail.some((email: string) => email === this.email.toLowerCase()) ||
          this.childResources.some((childResource: ChildResourcesEntity) => {
            return selectedResourceEmail.some((email: string) => email === childResource.email.toLowerCase());
          })
      );
    }
    return false;
  }

  isDisableResourceByHotDeskRule(selectedResource: ResourceEntity[], deskLocationList: DeskLocationEntity[]): boolean {
    const selectedResourceIds: number[] = selectedResource.map((resource) => resource.id);

    // Giả định nếu resource đang check được chọn
    if(!selectedResourceIds.includes(this.id)){
      selectedResourceIds.push(this.id);
    }

    for (const deskLocation of deskLocationList) {
      if (deskLocation.deskIds.includes(this.id)) {
        const selectedResourceIdsCount = selectedResourceIds.filter((num) => deskLocation.deskIds.includes(num)).length;
        if (selectedResourceIdsCount > deskLocation.remainingCapacity) {
          return true;
        }
      }
    }

    return false;
  }

  isDisableResourceByOpeningHoursRule(): boolean {
    return this.availability.isNotOpenYet;
  }

  toJson(): Partial<ResourceEntity> {
    return JSON.parse(JSON.stringify(this));
  }
}

export class CategoryInfo {
  id: number;
  caption: string;
  iconUrl?: string;

  constructor(data?: Partial<CategoryInfo>) {
    this.id = data?.id ?? 0;
    this.caption = data?.caption ?? "";
    this.iconUrl = data?.iconUrl ?? "";
  }
}

export class AvailabilityEntity {
  closeDates: string[];
  conflictDates: string[];
  exchangeByRuleType: number;
  isAvailable: boolean;
  isExceedCapacity: boolean;
  numberOfAvailable: number;
  permissionType: number;
  policyType: number;
  total: number;
  closingTimeType: number;
  openingTimeType: number;
  isOccurrencceExceedCapacity: boolean;
  isViolatePriority: boolean | null;
  unAvailableDates: string[];
  allowCapacity: number;
  remainingCapacity: number;
  violatePriorityDates: string[];
  violatePriorityDayOfWeeks: number[];
  isNotOpenYet: boolean;
  occurrenceCapacities: OccurrenceCapacityEntity[];
  allowRecurringMeetings: boolean;

  constructor(data?: Partial<AvailabilityEntity>, allowRecurringMeetings?: boolean) {
    this.closeDates = data?.closeDates ?? [];
    this.conflictDates = data?.conflictDates ?? [];
    this.exchangeByRuleType = data?.exchangeByRuleType ?? 0;
    this.isAvailable = data?.isAvailable ?? false;
    this.isExceedCapacity = data?.isExceedCapacity ?? false;
    this.numberOfAvailable = data?.numberOfAvailable ?? 0;
    this.permissionType = data?.permissionType ?? 0;
    this.policyType = data?.policyType ?? 0;
    this.total = data?.total ?? 0;
    this.closingTimeType = data?.closingTimeType ?? 0;
    this.openingTimeType = data?.openingTimeType ?? 0;
    this.isOccurrencceExceedCapacity = data?.isOccurrencceExceedCapacity ?? true;
    this.isViolatePriority = data?.isViolatePriority ?? null;
    this.unAvailableDates = data?.unAvailableDates ?? [];
    this.allowCapacity = data?.allowCapacity ?? 0;
    this.remainingCapacity = data?.remainingCapacity ?? 0;
    this.violatePriorityDates = data?.violatePriorityDates ?? [];
    this.violatePriorityDayOfWeeks = data?.violatePriorityDayOfWeeks ?? [];
    this.isNotOpenYet = !!data?.isNotOpenYet;
    this.occurrenceCapacities = data?.occurrenceCapacities ? data.occurrenceCapacities.map((item) => new OccurrenceCapacityEntity(item)) : [];
    this.allowRecurringMeetings = allowRecurringMeetings ?? true;
  }

  permissions(isRecurring?: boolean): EPermissionType[] | null {
    const permissionsClone: EPermissionType[] = [];
    const isRecurringWithConflicts = this.isAvailable && isRecurring && this.unAvailableDates.length > 0;
    const isRecurringWithOccurrenceCapacities = this.isAvailable && isRecurring && this.occurrenceCapacities.length > 0;
    const isUnavailable = !this.isAvailable;

    // Not allow recurring booking
    if (isUnavailable && isRecurring && !this.allowRecurringMeetings) {
      permissionsClone.push(EPermissionType.NotAllowRecurringBooking);
    }

    // Recurring với conflicts
    if (isRecurringWithConflicts) {
      if (this.permissionType === PermissionTypeEnum.Accept) {
        permissionsClone.push(EPermissionType.OccurenciesWithConflicts);
      }
      if (this.exchangeByRuleType === ExchangeByRuleTypeEnum.CanBookByExchangeByRule) {
        permissionsClone.push(EPermissionType.CanBookByExchangeByRule);
      }
    }

    // Recurring với occurrence capacities
    if (isRecurringWithOccurrenceCapacities) {
      if (this.isOccurrencceExceedCapacity) {
        permissionsClone.push(EPermissionType.OccurrencceExceedCapacity);
      }
    }

    // Available resources
    if (this.isAvailable) {
      // Delegate permissions
      if (
        (this.permissionType === PermissionTypeEnum.DelegateByInPolicy ||
          this.permissionType === PermissionTypeEnum.DelegateByOutPolicy)
      ) {
        permissionsClone.push(EPermissionType.DelegateByInOutPolicy);
      }
      // Prioritized reservation
      if (this.violatePriorityDayOfWeeks?.length > 0) {
        permissionsClone.push(EPermissionType.PrioritizedReservation);
      }
    }

    // Unavailable resources
    if (isUnavailable) {
      // Permission type based errors
      if (
        this.permissionType === PermissionTypeEnum.DeclinedByInPolicy ||
        this.permissionType === PermissionTypeEnum.DeniedInPolicyWorkspace
      ) {
        permissionsClone.push(EPermissionType.DeclinedByInPolicy);
      }
      if (this.permissionType === PermissionTypeEnum.DeclinedByOutPolicy) {
        permissionsClone.push(EPermissionType.DeclinedByOutPolicy);
      }
      if (this.permissionType === PermissionTypeEnum.VRConfigWrong) {
        permissionsClone.push(EPermissionType.VRConfigWrong);
      }

      // exceed capacity with all occurrences are unavailable
      if (this.isOccurrencceExceedCapacity && this.occurrenceCapacities.length > 0) {
        permissionsClone.push(EPermissionType.AllOccurrenceExceedCapacity);
      }

      // Capacity and exchange rule errors
      if (this.isExceedCapacity) {
        permissionsClone.push(EPermissionType.ExceedCapacity);
      }
      if (this.exchangeByRuleType === ExchangeByRuleTypeEnum.CannotBookByExchangeByRule) {
        permissionsClone.push(EPermissionType.CannotBookByExchangeByRule);
      }

      // Date and time related errors
      if (this.closingTimeType === ClosingTimeTypeEnum.CloseDate) {
        permissionsClone.push(EPermissionType.CloseDate);
      }
      if (this.isNotOpenYet) {
        permissionsClone.push(EPermissionType.OpenHours);
      }
      if (this.isViolatePriority) {
        permissionsClone.push(EPermissionType.ViolatePriority);
      }
      if ((this.permissionType === -1 || this.permissionType === 0 || this.permissionType === null)) {
        permissionsClone.push(EPermissionType.Unavailable);
      }
    }

    // Pre-close date (independent of availability)
    if (this.closingTimeType === ClosingTimeTypeEnum.PreCloseDate) {
      permissionsClone.push(EPermissionType.PreCloseDate);
    }

    return permissionsClone.length > 0 ? permissionsClone : null;
  }

  isCannotBook(isRecurring?: boolean): boolean {
    // if (isRecurring && this.isAvailable) {
    //   const permissions = this.permissions(isRecurring);
    //   if (
    //     (permissions?.length &&
    //       (permissions?.includes(EPermissionType.OccurenciesWithConflicts) ||
    //         permissions?.includes(EPermissionType.OccurrencceExceedCapacity) ||
    //         permissions?.includes(EPermissionType.CanBookByExchangeByRule))) ||
    //     permissions?.includes(EPermissionType.OpenHours)
    //   ) {
    //     return true;
    //   }
    // }
    return !this.isAvailable;
  }
}

export class OpenTimeEntity {
  openTime: string;
  openTimeOfDayInMinutes: number;
  openTimeTicks: number;

  constructor(data?: Partial<OpenTimeEntity>) {
    this.openTime = data?.openTime ?? "";
    this.openTimeOfDayInMinutes = data?.openTimeOfDayInMinutes ?? 0;
    this.openTimeTicks = data?.openTimeTicks ?? 0;
  }
}

export interface IGetResourceBookingRequest {
  requestId: string;
  organizer: string;
  timeZoneStandardName: string;
  attendees: string[];
  uid: string;
  globalId: string;
  isNoEndDate: boolean | null;
  isRecurring: boolean;
  occurrences: {
    utcStartDate: string;
    utcEndDate: string;
  }[];
  resourceIds?: number[];
}

export class GetSelectedResourceResponse {
  resources: ResourceEntity[];
  bookedMeetingLabel: {
    id: number;
    name: string;
  } | null;
  globalId: string | null;
  guid: string | null;
  haveOrderResources: number[];
  bookedVRResources: Record<string, number[]>;
  deskLocations: DeskLocationEntity[];

  constructor(data?: Partial<GetSelectedResourceResponse>) {
    this.resources = data?.resources ? data.resources.map((item) => new ResourceEntity(item)) : [];
    this.bookedMeetingLabel = data?.bookedMeetingLabel ?? null;
    this.globalId = data?.globalId || null;
    this.guid = data?.guid || null;
    this.haveOrderResources = data?.haveOrderResources ?? [];
    this.bookedVRResources = data?.bookedVRResources ?? {};
    this.deskLocations = data?.deskLocations ? data.deskLocations.map((item) => new DeskLocationEntity(item)) : [];
  }
}
export class ChildResourcesEntity {
  id: number;
  name: string;
  email: string;

  constructor(data?: Partial<ChildResourcesEntity>) {
    this.id = data?.id ?? 0;
    this.name = data?.name ?? "";
    this.email = data?.email?.toLowerCase().trim() ?? "";
  }
}

export interface IUserRestriction {
  forceOpenOrder: EForceOpenOrder;
  message: string;
}

export class DeskLocationEntity {
  allowCapacity: number;
  deskIds: number[];
  locationId: number;
  remainingCapacity: number;

  constructor(data?: Partial<DeskLocationEntity>) {
    this.allowCapacity = data?.allowCapacity ?? 0;
    this.deskIds = data?.deskIds ?? [];
    this.locationId = data?.locationId ?? 0;
    this.remainingCapacity = data?.remainingCapacity ?? 0;
  }
}

export class OccurrenceCapacityEntity {
  occurrence: string;
  allowCapacity: number;
  remainingCapacity: number;

  constructor(data?: Partial<OccurrenceCapacityEntity>) {
    this.occurrence = data?.occurrence ?? "";
    this.allowCapacity = data?.allowCapacity ?? 0;
    this.remainingCapacity = data?.remainingCapacity ?? 0;
  }
}

export interface IFilterResource {
  textSearch: string;
  status: boolean;
  capacityOperator: ComparisonOperator;
  capacityValue: number;
  sortBy: SortBy;
  categories: number[];
  categoryOperator: CategoryOperator;
  locations: number[];
  resourceType: string;
  isChangedLocations?: boolean;
}